import { ReactNode, Suspense, lazy } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useHeroContent } from "@/hooks/useHeroContent";
import HeroButtons from "@/components/ui/HeroButtons";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import CarmaxPageHero from "@/components/carmax/CarmaxPageHero";
import { useInteractionReady } from "./InteractionGate";

// Lazy-load the framer-motion text block so vendor-motion stays out of any
// route entry that imports PageHero. Mobile renders static markup and never
// touches this chunk at all.
const PageHeroAnimated = lazy(() => import("@/components/ui/PageHeroAnimated"));

interface PageHeroProps {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  overlay?: boolean;
  /**
   * Optional CMS slug. When provided, the component hydrates title/subtitle/
   * images/buttons from page_content (page_slug=<cmsSlug>, section_key='page_hero').
   * If the row is missing or the fetch fails, the hard-coded props above are used.
   */
  cmsSlug?: string;
}

const PageHero = ({
  title,
  subtitle,
  children,
  backgroundImage,
  backgroundImageMobile,
  overlay = true,
  cmsSlug,
}: PageHeroProps) => {
  const themeLayout = useThemeLayout();
  const isMobile = useIsMobile();
  const engaged = useInteractionReady();
  const { data: cms } = useHeroContent(cmsSlug ?? "", "page_hero");
  const cmsSlide = cmsSlug ? cms?.slides?.[0] : undefined;

  const effectiveTitle = cmsSlide?.title || title;
  const effectiveSubtitle = cmsSlide?.subtitle ?? subtitle;
  const effectiveDesktop = cmsSlide?.image_desktop || backgroundImage;
  const effectiveMobile = cmsSlide?.image_mobile || backgroundImageMobile;
  const cmsButtons = cmsSlide?.buttons;
  const overlayLevel = cms?.layout?.overlay; // off|light|medium|strong
  const showOverlay =
    overlayLevel === undefined ? overlay : overlayLevel !== "off";
  const overlayClass =
    overlayLevel === "light"
      ? "from-secondary/60 via-secondary/40 to-secondary/60"
      : overlayLevel === "strong"
      ? "from-secondary/95 via-secondary/85 to-secondary/95"
      : "from-secondary/90 via-secondary/70 to-secondary/90"; // medium / default

  // No JS-injected preload: it ran post-hydration (after the <picture> below
  // had already started the fetch) and on mobile pointed at the desktop URL
  // that never renders, which is exactly what triggers Chrome's "preloaded
  // using link preload but not used" warning. The eager <picture> is enough.

  if (themeLayout === "carmax") {
    return (
      <CarmaxPageHero
        {...(cmsSlug ? { cmsSlug } : {})}
        slides={[{
          image: backgroundImage || backgroundImageMobile || "",
          ...(backgroundImageMobile ? { imageMobile: backgroundImageMobile } : {}),
          title,
          ...(subtitle ? { subtitle } : {}),
          cta: children,
        }]}
      />
    );
  }

  return (
    <section className="relative flex min-h-[320px] items-center justify-center overflow-hidden bg-secondary -mt-[140px] pt-[140px]">
      {effectiveDesktop && (
        <picture className="absolute inset-0 h-full w-full">
          {effectiveMobile && (
            <source srcSet={effectiveMobile} media="(max-width: 768px)" type="image/webp" />
          )}
          <source srcSet={effectiveDesktop} media="(min-width: 769px)" type="image/webp" />
          <img
            src={effectiveMobile ?? effectiveDesktop}
            alt=""
            className="h-full w-full object-cover"
            fetchPriority={"high"}
            loading="eager"
            decoding="sync"
          />
        </picture>
      )}
      {showOverlay && <div className={`absolute inset-0 bg-gradient-to-b ${overlayClass}`} />}
      <div className="relative z-10 container py-16 text-center">
        {isMobile || !engaged ? (
          <>
            <h1 className="font-heading text-4xl font-bold uppercase tracking-wide text-primary-foreground md:text-5xl lg:text-6xl">
              {effectiveTitle}
            </h1>
            {effectiveSubtitle && (
              <p className="mx-auto mt-4 max-w-2xl text-lg text-secondary-foreground/70">
                {effectiveSubtitle}
              </p>
            )}
            <div className="mt-8">
              <HeroButtons {...(cmsButtons !== undefined ? { buttons: cmsButtons } : {})} fallback={children} />
            </div>
          </>
        ) : (
          <Suspense
            fallback={
              <>
                <h1 className="font-heading text-4xl font-bold uppercase tracking-wide text-primary-foreground md:text-5xl lg:text-6xl">
                  {effectiveTitle}
                </h1>
                {effectiveSubtitle && (
                  <p className="mx-auto mt-4 max-w-2xl text-lg text-secondary-foreground/70">
                    {effectiveSubtitle}
                  </p>
                )}
                <div className="mt-8">
                  <HeroButtons {...(cmsButtons !== undefined ? { buttons: cmsButtons } : {})} fallback={children} />
                </div>
              </>
            }
          >
            <PageHeroAnimated
              title={effectiveTitle}
              {...(effectiveSubtitle !== undefined ? { subtitle: effectiveSubtitle } : {})}
              {...(cmsButtons !== undefined ? { buttons: cmsButtons } : {})}
              fallback={children}
            />
          </Suspense>
        )}
      </div>
    </section>
  );
};

export default PageHero;
